import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import Typography from "../Typography/Typography";
import Button from "../Button/Button";

export default function FullPageModal({ title, open, onClose, children }) {
  const sheetRef = useRef(null);
  const startY = useRef(0);
  const [isVisible, setIsVisible] = useState(open);

  // Sincronizza lo stato interno con la prop `open`
  useEffect(() => {
    if (open) {
      setIsVisible(true); // Se `open` è true, mostra subito il modal
    } else {
      setIsVisible(false); // Se `open` è false, nascondi il modal
    }
  }, [open]);

  // Chiude con animazione invece di rimuoverlo subito
  const handleClose = () => {
    console.log("chiuso")
    setIsVisible(false);
    setTimeout(onClose, 200); // Aspetta l'animazione prima di rimuoverlo
  };

  // Swipe per chiudere su mobile
  useEffect(() => {
    const handleTouchStart = (e) => {
      startY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      if (e.touches[0].clientY - startY.current > 50) {
        handleClose();
      }
    };

    if (open) {
      window.addEventListener("touchstart", handleTouchStart);
      window.addEventListener("touchmove", handleTouchMove);
    }

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, [open]);

  return (
    <>
      {/* Overlay */}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
        )}
      </AnimatePresence>

      {/* Modal classico su desktop */}
      <AnimatePresence>
        {isVisible && (
          <div className="hidden md:flex fixed inset-0 z-50 items-center justify-center" onClick={handleClose}>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="relative w-full max-w-[52em] h-full max-h-[500px] flex flex-col gap-6 justify-between bg-white rounded-[18px] border-2 border-gray-150 shadow-lg px-9 py-7"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-row justify-between items-center">
                <Typography size="32" weight="sb" family="serif">
                  {title}
                </Typography>
                <Button type="bw" icon={<X size={20} />} onClick={handleClose} />
              </div>
              {children}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Bottom Sheet su mobile */}
      <div className="md:hidden fixed bottom-20 left-0 w-full z-20">
        <AnimatePresence>
          {isVisible && (
            <motion.div
              ref={sheetRef}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="bg-white rounded-t-2xl shadow-lg px-4 py-4 pt-3 w-full max-w-md mx-auto"
            >
              {/* Drag Handle */}
              <div className="w-12 h-1 bg-gray-400 rounded-full mx-auto" />

              <div className="my-5">
                <Typography size="24" weight="sb" family="serif">{title}</Typography>
              </div>

              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}